import React, { useState } from "react";
import {
  FiHelpCircle,
  FiMail,
  FiMessageCircle,
  FiFileText,
  FiExternalLink,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { Link } from "react-router-dom";

// Define types for support section
interface SupportSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const ProfileHelpSupport = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  const supportSections: SupportSection[] = [
    {
      id: "account",
      title: "Account Settings",
      icon: <FiFileText size={20} />,
      content: (
        <div className="space-y-3">
          <p className="text-gray-700">
            Need help with your account settings? Here's how to manage your
            profile:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Update notification preferences in the Profile Settings tab</li>
            <li>Change your password in the Change Password tab in profile</li>
            <li>
              Update your subscription package by clicking on the subscription
              tab in the profile section
            </li>
            <li>
              Manage your privacy settings in the Privacy & Security section
            </li>
          </ul>
          <a
            href="/profile/help"
            className="inline-flex items-center text-gray-7 hover:text-gray-6 font-semibold"
          >
            Read full documentation{" "}
            <FiExternalLink size={16} className="ml-1" />
          </a>
        </div>
      ),
    },
    {
      id: "faq",
      title: "Frequently Asked Questions",
      icon: <FiHelpCircle size={20} />,
      content: (
        <div className="space-y-3">
          <div className="space-y-2">
            <p className="font-medium">How do I reset my password?</p>
            <p className="text-gray-700">
              Go to the Login page and click "Forgot password". Follow the
              instructions sent to your email.
            </p>
          </div>
          <div className="space-y-2">
            <p className="font-medium">How can I delete my account?</p>
            <p className="text-gray-700">
              Go to{" "}
              <Link className="font-semibold" to="/profile">
                Profile
              </Link>{" "}
              &gt;{" "}
              <Link className="font-semibold" to="/profile/settings">
                Settings
              </Link>{" "}
              &gt;{" "}
              <Link className="font-semibold" to="/profile/request-delete">
                Request Account Deletion
              </Link>
              . Please note this action cannot be undone once completed.
            </p>
          </div>
          {/* <div className="space-y-2">
            <p className="font-medium">Where can I see my activity history?</p>
            <p className="text-gray-700">
              Your activity log is accessible from your profile page under the
              "Activity" tab.
            </p>
          </div> */}
        </div>
      ),
    },
    {
      id: "contact",
      title: "Contact Support Team",
      icon: <FiMessageCircle size={20} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Our support team is available Monday through Friday, 9am-5pm EST.
          </p>
          <div className="space-y-3">
            {/* Implement smartsupp chat */}
            <a
              href="/#contact"
              className="block w-full py-2 px-4 text-center bg-gray-7 hover:bg-gray-6 text-white rounded-md font-medium transition-colors"
            >
              Start a Live Chat
            </a>
            <a
              href="mailto:info@unspokenn.com"
              className="flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md hover:bg-gray-50 text-gray-800 font-medium transition-colors"
            >
              <FiMail size={18} className="mr-2" />
              Email Support Team
            </a>
          </div>
          <p className="text-sm text-gray-500">
            Expected response time: Within 24 hours
          </p>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full max-w-2xl mx-auto mt-6 bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-5 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-7">
          Profile Help & Support
        </h2>
        <p className="mt-1 text-gray-600">
          Find answers to common questions or contact our support team
        </p>
      </div>

      <div className="divide-y divide-gray-200">
        {supportSections.map((section) => (
          <div key={section.id} className="p-5">
            <button
              onClick={() => toggleSection(section.id)}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center">
                <span className="mr-3 text-gray-7">{section.icon}</span>
                <span className="font-medium text-gray-800">
                  {section.title}
                </span>
              </div>
              {expandedSection === section.id ? (
                <FiChevronUp size={20} className="text-gray-4" />
              ) : (
                <FiChevronDown size={20} className="text-gray-4" />
              )}
            </button>

            {expandedSection === section.id && (
              <div className="mt-4 pl-8">{section.content}</div>
            )}
          </div>
        ))}
      </div>

      <div className="p-5 bg-gray-50 rounded-b-lg border-t border-gray-200">
        <p className="text-sm text-gray-600 text-center">
          Can't find what you're looking for? Visit our{" "}
          <a
            href="/profile/help"
            className="text-gray-7 hover:underline font-semibold"
          >
            Help Center
          </a>{" "}
          for more resources.
        </p>
      </div>
    </div>
  );
};

export default ProfileHelpSupport;
